import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Settings as SettingsIcon, Terminal, Key, Save, Eye, EyeOff, Loader2, Shield, Server } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SettingsPage() {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = "Settings | GeekTickets POS";
  }, []);
  
  // Terminal settings
  const [terminalId, setTerminalId] = useState("");
  const [sandboxSubAccountId, setSandboxSubAccountId] = useState("");
  const [productionSubAccountId, setProductionSubAccountId] = useState("");
  
  // API settings
  const [apiEnvironment, setApiEnvironment] = useState("sandbox");
  const [sandboxApiKey, setSandboxApiKey] = useState("");
  const [productionApiKey, setProductionApiKey] = useState("");
  const [sandboxPublicKey, setSandboxPublicKey] = useState("");
  const [productionPublicKey, setProductionPublicKey] = useState("");
  const [sandboxApiKeyPreview, setSandboxApiKeyPreview] = useState("");
  const [productionApiKeyPreview, setProductionApiKeyPreview] = useState("");
  const [sandboxPublicKeyPreview, setSandboxPublicKeyPreview] = useState("");
  const [productionPublicKeyPreview, setProductionPublicKeyPreview] = useState("");
  const [hasSandboxKey, setHasSandboxKey] = useState(false);
  const [hasProductionKey, setHasProductionKey] = useState(false);
  const [hasSandboxPublicKey, setHasSandboxPublicKey] = useState(false);
  const [hasProductionPublicKey, setHasProductionPublicKey] = useState(false);
  const [showSandboxKey, setShowSandboxKey] = useState(false);
  const [showProductionKey, setShowProductionKey] = useState(false);
  const [showSandboxPublicKey, setShowSandboxPublicKey] = useState(false);
  const [showProductionPublicKey, setShowProductionPublicKey] = useState(false);
  
  // Password settings
  const [adminPassword, setAdminPassword] = useState("");
  const [confirmAdminPassword, setConfirmAdminPassword] = useState("");
  const [posPassword, setPosPassword] = useState("");
  const [confirmPosPassword, setConfirmPosPassword] = useState("");
  const [motoPassword, setMotoPassword] = useState("");
  const [confirmMotoPassword, setConfirmMotoPassword] = useState("");
  const [showAdminPassword, setShowAdminPassword] = useState(false);
  const [showPosPassword, setShowPosPassword] = useState(false);
  const [showMotoPassword, setShowMotoPassword] = useState(false);
  const [hasMotoPassword, setHasMotoPassword] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`, {
        headers: getAuthHeaders()
      });
      setTerminalId(res.data.terminal_id || "");
      setSandboxSubAccountId(res.data.sandbox_sub_account_id || "");
      setProductionSubAccountId(res.data.production_sub_account_id || "");
      setApiEnvironment(res.data.api_environment || "sandbox");
      setSandboxApiKeyPreview(res.data.sandbox_api_key_preview || "");
      setProductionApiKeyPreview(res.data.production_api_key_preview || "");
      setSandboxPublicKeyPreview(res.data.sandbox_public_key_preview || "");
      setProductionPublicKeyPreview(res.data.production_public_key_preview || "");
      setHasSandboxKey(res.data.has_sandbox_api_key);
      setHasProductionKey(res.data.has_production_api_key);
      setHasSandboxPublicKey(res.data.has_sandbox_public_key);
      setHasProductionPublicKey(res.data.has_production_public_key);
      setHasMotoPassword(res.data.has_moto_password);
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTerminalSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, {
        terminal_id: terminalId,
        sandbox_sub_account_id: sandboxSubAccountId,
        production_sub_account_id: productionSubAccountId
      }, {
        headers: getAuthHeaders()
      });
      toast.success("Terminal settings saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveApiSettings = async () => {
    setSaving(true);
    try {
      const updates = {
        api_environment: apiEnvironment
      };
      
      if (sandboxApiKey) {
        updates.sandbox_api_key = sandboxApiKey;
      }
      
      if (productionApiKey) {
        updates.production_api_key = productionApiKey;
      }
      
      if (sandboxPublicKey) {
        updates.sandbox_public_key = sandboxPublicKey;
      }
      
      if (productionPublicKey) {
        updates.production_public_key = productionPublicKey;
      }
      
      await axios.put(`${API}/settings`, updates, {
        headers: getAuthHeaders()
      });
      
      toast.success("API settings saved successfully");
      
      // Refresh to get updated previews
      await fetchSettings();
      
      // Clear the input fields
      setSandboxApiKey("");
      setProductionApiKey("");
      setSandboxPublicKey("");
      setProductionPublicKey("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save API settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAdminPassword = async () => {
    if (!adminPassword.trim()) {
      toast.error("Please enter a new admin password");
      return;
    }

    if (adminPassword.length < 4) {
      toast.error("Password must be at least 4 characters");
      return;
    }

    if (adminPassword !== confirmAdminPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/settings`, {
        admin_password: adminPassword
      }, {
        headers: getAuthHeaders()
      });
      toast.success("Admin password updated successfully");
      setAdminPassword("");
      setConfirmAdminPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update password");
    } finally {
      setSaving(false);
    }
  };

  const handleSavePosPassword = async () => {
    if (!posPassword.trim()) {
      toast.error("Please enter a new POS password");
      return;
    }

    if (posPassword.length < 4) {
      toast.error("Password must be at least 4 characters");
      return;
    }

    if (posPassword !== confirmPosPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/settings`, {
        pos_password: posPassword
      }, {
        headers: getAuthHeaders()
      });
      toast.success("POS password updated successfully");
      setPosPassword("");
      setConfirmPosPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update password");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMotoPassword = async () => {
    if (!motoPassword.trim()) {
      toast.error("Please enter a new MOTO password");
      return;
    }

    if (motoPassword.length < 4) {
      toast.error("Password must be at least 4 characters");
      return;
    }

    if (motoPassword !== confirmMotoPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/settings`, {
        moto_password: motoPassword
      }, {
        headers: getAuthHeaders()
      });
      toast.success("MOTO password updated successfully");
      setMotoPassword("");
      setConfirmMotoPassword("");
      setHasMotoPassword(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update password");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
          <SettingsIcon className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold">Settings</h1>
          <p className="text-sm text-muted-foreground">Configure terminal, API, and security settings</p>
        </div>
      </div>

      {/* Terminal Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Terminal className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">Terminal Configuration</CardTitle>
          </div>
          <CardDescription>
            Configure the terminal ID and sub-accounts for processing payments
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="terminal-id">Terminal ID</Label>
            <Input
              id="terminal-id"
              value={terminalId}
              onChange={(e) => setTerminalId(e.target.value)}
              placeholder="tml_XXXXXXXXXXXXXXXXXXXXXXXXX"
              data-testid="settings-terminal-id"
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              The unique identifier for your terminal (starts with tml_)
            </p>
          </div>

          <Separator />

          <div className="space-y-2">
            <Label htmlFor="sandbox-sub-account-id">Sandbox Sub-Account ID</Label>
            <Input
              id="sandbox-sub-account-id"
              value={sandboxSubAccountId}
              onChange={(e) => setSandboxSubAccountId(e.target.value)}
              placeholder="ac_XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"
              data-testid="settings-sandbox-sub-account-id"
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Sub-account ID for sandbox/test transactions (starts with ac_)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="production-sub-account-id">Production Sub-Account ID</Label>
            <Input
              id="production-sub-account-id"
              value={productionSubAccountId}
              onChange={(e) => setProductionSubAccountId(e.target.value)}
              placeholder="ac_XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"
              data-testid="settings-production-sub-account-id"
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Sub-account ID for production/live transactions (starts with ac_)
            </p>
          </div>

          <Button 
            onClick={handleSaveTerminalSettings} 
            disabled={saving}
            data-testid="save-terminal-settings-btn"
            className="w-full sm:w-auto"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Terminal Settings
          </Button>
        </CardContent>
      </Card>

      {/* API Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Server className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">API Configuration</CardTitle>
          </div>
          <CardDescription>
            Configure API environment and secret keys
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Environment Toggle */}
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div className="space-y-1">
              <Label className="text-base font-medium">API Environment</Label>
              <p className="text-sm text-muted-foreground">
                {apiEnvironment === "production" 
                  ? "Using production API (live transactions)" 
                  : "Using sandbox API (test transactions)"}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Badge 
                variant="outline" 
                className={apiEnvironment === "sandbox" 
                  ? "bg-yellow-100 text-yellow-800 border-yellow-200" 
                  : "bg-green-100 text-green-800 border-green-200"
                }
              >
                {apiEnvironment === "sandbox" ? "Sandbox" : "Production"}
              </Badge>
              <Switch
                checked={apiEnvironment === "production"}
                onCheckedChange={(checked) => setApiEnvironment(checked ? "production" : "sandbox")}
                data-testid="api-environment-toggle"
              />
            </div>
          </div>

          {apiEnvironment === "production" && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800 font-medium">⚠️ Production Mode Active</p>
              <p className="text-xs text-red-600 mt-1">
                All transactions will be processed as live payments. Use with caution.
              </p>
            </div>
          )}

          <Separator />

          {/* Sandbox API Key */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Sandbox Secret Key</Label>
              {hasSandboxKey && (
                <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200 text-xs">
                  Configured
                </Badge>
              )}
            </div>
            
            {sandboxApiKeyPreview && (
              <div className="p-2 bg-muted rounded text-xs font-mono text-muted-foreground">
                Current: {sandboxApiKeyPreview}
              </div>
            )}
            
            <div className="relative">
              <Input
                type={showSandboxKey ? "text" : "password"}
                value={sandboxApiKey}
                onChange={(e) => setSandboxApiKey(e.target.value)}
                placeholder={hasSandboxKey ? "Enter new key to replace" : "sk_sandbox_..."}
                data-testid="settings-sandbox-api-key"
                className="font-mono text-sm pr-10"
              />
              <button
                type="button"
                onClick={() => setShowSandboxKey(!showSandboxKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showSandboxKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Your sandbox secret API key for test transactions
            </p>
          </div>

          {/* Sandbox Public Key */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Sandbox Public Key</Label>
              {hasSandboxPublicKey && (
                <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200 text-xs">
                  Configured
                </Badge>
              )}
            </div>
            
            {sandboxPublicKeyPreview && (
              <div className="p-2 bg-muted rounded text-xs font-mono text-muted-foreground">
                Current: {sandboxPublicKeyPreview}
              </div>
            )}
            
            <div className="relative">
              <Input
                type={showSandboxPublicKey ? "text" : "password"}
                value={sandboxPublicKey}
                onChange={(e) => setSandboxPublicKey(e.target.value)}
                placeholder={hasSandboxPublicKey ? "Enter new key to replace" : "pk_sandbox_..."}
                data-testid="settings-sandbox-public-key"
                className="font-mono text-sm pr-10"
              />
              <button
                type="button"
                onClick={() => setShowSandboxPublicKey(!showSandboxPublicKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showSandboxPublicKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Your sandbox public API key (required for MOTO payments)
            </p>
          </div>

          <Separator />

          {/* Production API Key */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Production Secret Key</Label>
              {hasProductionKey && (
                <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200 text-xs">
                  Configured
                </Badge>
              )}
            </div>
            
            {productionApiKeyPreview && (
              <div className="p-2 bg-muted rounded text-xs font-mono text-muted-foreground">
                Current: {productionApiKeyPreview}
              </div>
            )}
            
            <div className="relative">
              <Input
                type={showProductionKey ? "text" : "password"}
                value={productionApiKey}
                onChange={(e) => setProductionApiKey(e.target.value)}
                placeholder={hasProductionKey ? "Enter new key to replace" : "sk_..."}
                data-testid="settings-production-api-key"
                className="font-mono text-sm pr-10"
              />
              <button
                type="button"
                onClick={() => setShowProductionKey(!showProductionKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showProductionKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Your production secret API key for live transactions
            </p>
          </div>

          {/* Production Public Key */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Production Public Key</Label>
              {hasProductionPublicKey && (
                <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200 text-xs">
                  Configured
                </Badge>
              )}
            </div>
            
            {productionPublicKeyPreview && (
              <div className="p-2 bg-muted rounded text-xs font-mono text-muted-foreground">
                Current: {productionPublicKeyPreview}
              </div>
            )}
            
            <div className="relative">
              <Input
                type={showProductionPublicKey ? "text" : "password"}
                value={productionPublicKey}
                onChange={(e) => setProductionPublicKey(e.target.value)}
                placeholder={hasProductionPublicKey ? "Enter new key to replace" : "pk_..."}
                data-testid="settings-production-public-key"
                className="font-mono text-sm pr-10"
              />
              <button
                type="button"
                onClick={() => setShowProductionPublicKey(!showProductionPublicKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showProductionPublicKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Your production public API key (required for MOTO payments)
            </p>
          </div>

          <Button 
            onClick={handleSaveApiSettings} 
            disabled={saving}
            data-testid="save-api-settings-btn"
            className="w-full sm:w-auto"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save API Settings
          </Button>
        </CardContent>
      </Card>

      {/* Password Settings */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Key className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">Security Settings</CardTitle>
          </div>
          <CardDescription>
            Update admin and POS passwords (passwords are securely hashed)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Admin Password */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm">Admin Password</h3>
            <div className="space-y-2">
              <Label htmlFor="new-admin-password">New Admin Password</Label>
              <div className="relative">
                <Input
                  id="new-admin-password"
                  type={showAdminPassword ? "text" : "password"}
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="Enter new admin password"
                  data-testid="settings-admin-password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowAdminPassword(!showAdminPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showAdminPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-admin-password">Confirm Admin Password</Label>
              <Input
                id="confirm-admin-password"
                type={showAdminPassword ? "text" : "password"}
                value={confirmAdminPassword}
                onChange={(e) => setConfirmAdminPassword(e.target.value)}
                placeholder="Confirm new admin password"
                data-testid="settings-confirm-admin-password"
              />
            </div>
            <Button 
              onClick={handleSaveAdminPassword} 
              disabled={saving || !adminPassword}
              variant="outline"
              data-testid="save-admin-password-btn"
              className="w-full sm:w-auto"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Update Admin Password
            </Button>
          </div>

          <Separator />

          {/* POS Password */}
          <div className="space-y-4">
            <h3 className="font-medium text-sm">POS Password</h3>
            <div className="space-y-2">
              <Label htmlFor="new-pos-password">New POS Password</Label>
              <div className="relative">
                <Input
                  id="new-pos-password"
                  type={showPosPassword ? "text" : "password"}
                  value={posPassword}
                  onChange={(e) => setPosPassword(e.target.value)}
                  placeholder="Enter new POS password"
                  data-testid="settings-pos-password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPosPassword(!showPosPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPosPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-pos-password">Confirm POS Password</Label>
              <Input
                id="confirm-pos-password"
                type={showPosPassword ? "text" : "password"}
                value={confirmPosPassword}
                onChange={(e) => setConfirmPosPassword(e.target.value)}
                placeholder="Confirm new POS password"
                data-testid="settings-confirm-pos-password"
              />
            </div>
            <Button 
              onClick={handleSavePosPassword} 
              disabled={saving || !posPassword}
              variant="outline"
              data-testid="save-pos-password-btn"
              className="w-full sm:w-auto"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Update POS Password
            </Button>
          </div>

          <Separator />

          {/* MOTO Password */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-sm">MOTO Password</h3>
              {hasMotoPassword && (
                <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-200 text-xs">
                  Configured
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Separate password for MOTO (phone order) access. Sessions expire after 30 minutes.
            </p>
            <div className="space-y-2">
              <Label htmlFor="new-moto-password">New MOTO Password</Label>
              <div className="relative">
                <Input
                  id="new-moto-password"
                  type={showMotoPassword ? "text" : "password"}
                  value={motoPassword}
                  onChange={(e) => setMotoPassword(e.target.value)}
                  placeholder="Enter new MOTO password"
                  data-testid="settings-moto-password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowMotoPassword(!showMotoPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showMotoPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-moto-password">Confirm MOTO Password</Label>
              <Input
                id="confirm-moto-password"
                type={showMotoPassword ? "text" : "password"}
                value={confirmMotoPassword}
                onChange={(e) => setConfirmMotoPassword(e.target.value)}
                placeholder="Confirm new MOTO password"
                data-testid="settings-confirm-moto-password"
              />
            </div>
            <Button 
              onClick={handleSaveMotoPassword} 
              disabled={saving || !motoPassword}
              variant="outline"
              data-testid="save-moto-password-btn"
              className="w-full sm:w-auto"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {hasMotoPassword ? "Update MOTO Password" : "Set MOTO Password"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-muted/50 border-dashed">
        <CardContent className="pt-6">
          <div className="text-sm text-muted-foreground space-y-3">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span className="font-medium text-foreground">Security Information</span>
            </div>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li><strong>Passwords:</strong> Securely hashed using bcrypt (one-way encryption)</li>
              <li><strong>API Keys:</strong> Encrypted using AES-256 before storage</li>
              <li><strong>Key Display:</strong> First 10 and last 10 characters shown for verification</li>
              <li><strong>Admin:</strong> Full access, session expires after 10 minutes</li>
              <li><strong>POS:</strong> Payments only, session stays active until logout</li>
              <li><strong>MOTO:</strong> Phone orders only, session expires after 30 minutes</li>
              <li><strong>Public Key:</strong> Required for MOTO (card-not-present) payments</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
